import * as React from "react";
import * as DTO from "../../../interfaces/dtos";
import { RouteComponentProps, withRouter } from "react-router-dom";

import { PageActionProps, PageTitle } from "@/components";

import { VideoScriptsServices } from "@/services";
import { useCallback, useMemo, useState } from "react";
import { VideoScriptForm } from "./components/VideoScriptForm";
import { VideoScriptRequest } from "@/interfaces";
import { useAccountSettings, URLS, useDeleteVideoScript, useSaveVideoScript, useSocialCoachSession } from "@/utils";
import { ScriptDetails } from "./components/VideoScriptDetails";
import { NotificationModal } from "./components/NotificationModal";
import { PageWrapper } from "@/components/PageWrapper";
import { toast } from "sonner";

type Props = RouteComponentProps<{ id: string }> & {
  embedded?: boolean;
};

const VideoScriptFC: React.FC<Props> = ({
  embedded = false,
  history,
  match: {
    params: { id },
  },
}) => {
  const { isAdmin, isEditor, isCoach, coachId } = useSocialCoachSession();
  const [videoScript, setVideoScript] = useState<DTO.VideoScript>();
  const [loading, setLoading] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [values, setValues] = useState<any>();

  const urls = isAdmin ? URLS : isEditor ? URLS.editor : URLS.coach;
  const { hasWritePermission } = useAccountSettings();
  const withWritePermission = hasWritePermission(DTO.WebComponents.MANAGE_SCRIPT_LIBRARY);
  const isNew = id === "new";
  const paramScriptId = isNew ? undefined : Number(id);

  const { mutateAsync: deleteAsync } = useDeleteVideoScript();
  const { mutateAsync: saveAsync } = useSaveVideoScript();

  React.useEffect(() => {
    const getScript = async (scriptId: number) => {
      const result = await VideoScriptsServices.get(scriptId);
      setVideoScript(result);
    };

    if (paramScriptId) {
      getScript(paramScriptId).catch(console.error);
    } else {
      setVideoScript({
        coachId: isAdmin ? -1 : coachId,
        title: "",
        content: "",
        categories: [],
        industryRoles: [],
      });
    }
  }, [paramScriptId]);

  const deleteScript = useCallback(async () => {
    if (window.confirm("Are you sure you want to delete this script?")) {
      try {
        setLoading(true);
        await deleteAsync(videoScript?.videoScriptId!!);
        toast.success("Script Deleted!");
        history.push(urls.videoScripts);
      } catch (e: any) {
        toast.error(`Error deleting script: ${e}`);
        console.error(e);
      } finally {
        setLoading(false);
      }
    }
  }, [videoScript?.videoScriptId, deleteAsync, history]);

  const saveScript = useCallback(async () => {
    try {
      setLoading(true);
      const request: VideoScriptRequest = {
        coachId: values.coachId,
        title: values.title,
        content: values.content,
        categories: values.categories,
        industryRoles: values.industryRoles,
      };
      const updatedScript = await saveAsync({
        id: videoScript?.videoScriptId,
        request,
      });
      setVideoScript(updatedScript);
      toast.success("Saved!");
      if (updatedScript.videoScriptId !== values?.videoScriptId) {
        history.replace(urls.videoScript.replace(":id", `${updatedScript.videoScriptId}`));
      }
    } catch (e: any) {
      console.error(e);
      toast.error(`Error saving script: ${e}`);
    } finally {
      setLoading(false);
    }
  }, [values, saveAsync, history]);

  const actions = useMemo(() => {
    const actionList: PageActionProps[] = [];
    if (isAdmin || withWritePermission) {
      actionList.push({
        text: "Save",
        onClick: saveScript,
        disabled: !isValid,
        loading,
      });
      if (videoScript?.videoScriptId) {
        if (isCoach && !isAdmin) {
          actionList.push({
            text: "Send Script Notification",
            onClick: () => setShowNotificationModal(true),
            loading,
          });
          actionList.push({
            text: "New Video Page from Script",
            onClick: () => {
              history.push(URLS.coach.recordVideo, {
                script: values?.content,
                id: videoScript?.videoScriptId,
                purpose: "videoPage",
              });
            },
            loading,
          });
        }
        actionList.push({
          text: "Delete",
          onClick: deleteScript,
          loading,
        });
      }
    }
    return actionList;
  }, [
    isAdmin,
    withWritePermission,
    isValid,
    loading,
    videoScript?.videoScriptId,
    history,
    values?.content,
    saveScript,
    deleteScript,
  ]);

  const content = (
    <>
      <div className="flex items-start max-w-[1300px]">
        {videoScript && (
          <div className="w-1/2">
            <VideoScriptForm
              videoScript={videoScript}
              updatingHandler={setValues}
              validationHandler={setIsValid}
              disabled={!isAdmin && !withWritePermission}
            />
          </div>
        )}

        {videoScript?.videoScriptId && (
          <div className="w-1/2">
            <ScriptDetails videoScriptId={videoScript?.videoScriptId} />
          </div>
        )}
      </div>

      {showNotificationModal && videoScript && (
        <NotificationModal
          opened={showNotificationModal}
          script={videoScript}
          acceptHandler={() => setShowNotificationModal(false)}
          rejectHandler={() => setShowNotificationModal(false)}
        />
      )}
    </>
  );

  if (embedded) {
    return (
      <div>
        <PageTitle
          title="Video Script"
          actions={actions}
          hideSeparator
          breadcrumbs={[{ name: "Library", link: URLS.coach.videoScripts }]}
        />
        {content}
      </div>
    );
  } else {
    return (
      <PageWrapper title="Video Script" actions={actions}>
        {content}
      </PageWrapper>
    );
  }
};

export const VideoScriptPage = withRouter(VideoScriptFC);
